import {
  FinanceInvestigationReceiptState,
  FinanceInvestigationReconciliationState,
  type FinanceTransactionInvestigationFacts,
} from "./finance-investigation";

/**
 * The rules behind an investigation's checks ("Vérifications"), shared by the page and the PDF.
 *
 * The tenant app states them in the reader's language on screen and the API prints them in the investigation
 * PDF. Both read WHICH checks apply and WHAT state each is in from here, so the sheet and the document can never
 * disagree on a point being open. Only the words differ by runtime.
 */

/**
 * An entry typed in this many days or more after the money moved is flagged as late. A month covers the normal
 * "we enter last Sunday's offerings on Wednesday" lag and the usual month-end batch, so only genuine catch-up
 * entries (the ones an auditor asks about) are raised.
 */
export const LATE_ENTRY_THRESHOLD_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days between the operation and its entry. Never negative: an entry made "before" is not late. */
export function entryDelayDays(occurredAt: string, recordedAt: string): number {
  const occurred = new Date(occurredAt).getTime();
  const recorded = new Date(recordedAt).getTime();
  if (Number.isNaN(occurred) || Number.isNaN(recorded)) return 0;
  return Math.max(0, Math.floor((recorded - occurred) / DAY_MS));
}

export function isLateEntry(days: number): boolean {
  return days >= LATE_ENTRY_THRESHOLD_DAYS;
}

export enum InvestigationCheckId {
  Balanced = "balanced",
  LateEntry = "lateEntry",
  Evidence = "evidence",
  Receipt = "receipt",
  Reconciliation = "reconciliation",
}

export enum InvestigationCheckStatus {
  /** Nothing to do. */
  Ok = "ok",
  /** Something looks wrong and deserves a look. */
  Warning = "warning",
  /** A step that has not been done yet. */
  Todo = "todo",
}

export type InvestigationCheckState = { id: InvestigationCheckId; status: InvestigationCheckStatus };

/**
 * Every check that applies to the investigation, in display order, with its state. Checks that do not apply (a
 * donor receipt on an expense, a reconciliation without a treasury account) are left out rather than passed.
 */
export function investigationCheckStates(
  accountingImpact: { balanced: boolean } | null | undefined,
  facts: FinanceTransactionInvestigationFacts | null | undefined,
): InvestigationCheckState[] {
  const states: InvestigationCheckState[] = [];
  if (accountingImpact) {
    states.push({
      id: InvestigationCheckId.Balanced,
      status: accountingImpact.balanced ? InvestigationCheckStatus.Ok : InvestigationCheckStatus.Warning,
    });
  }
  if (!facts) return states;

  states.push({
    id: InvestigationCheckId.LateEntry,
    status: isLateEntry(entryDelayDays(facts.occurredAt, facts.recordedAt))
      ? InvestigationCheckStatus.Warning
      : InvestigationCheckStatus.Ok,
  });

  if (facts.evidence.applicable) {
    states.push({
      id: InvestigationCheckId.Evidence,
      status: facts.evidence.present ? InvestigationCheckStatus.Ok : InvestigationCheckStatus.Warning,
    });
  }

  if (facts.receipt.applicable) {
    states.push({
      id: InvestigationCheckId.Receipt,
      status:
        facts.receipt.state === FinanceInvestigationReceiptState.Issued
          ? InvestigationCheckStatus.Ok
          : facts.receipt.state === FinanceInvestigationReceiptState.Cancelled
            ? InvestigationCheckStatus.Warning
            : InvestigationCheckStatus.Todo,
    });
  }

  if (facts.reconciliation) {
    states.push({
      id: InvestigationCheckId.Reconciliation,
      status:
        facts.reconciliation.state === FinanceInvestigationReconciliationState.Covered
          ? InvestigationCheckStatus.Ok
          : InvestigationCheckStatus.Todo,
    });
  }

  return states;
}

/** The open points: everything not marked done. */
export function openInvestigationCheckCount(states: readonly { status: InvestigationCheckStatus }[]): number {
  return states.filter((state) => state.status !== InvestigationCheckStatus.Ok).length;
}
